import React, { useState, useEffect } from 'react';
import { certificatesApi, companiesApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { MagnifyingGlass, Download, Certificate, Calendar as CalendarIcon, XCircle, Eye, Buildings, UsersThree } from '@phosphor-icons/react';
import { format, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import CourseCountBadge from '../../components/CourseCountBadge';

export default function CertificatesPage() {
  const [certificates, setCertificates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [mandantes, setMandantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewCert, setPreviewCert] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Consolidated tabs state
  const [consCompanyDate, setConsCompanyDate] = useState(null);
  const [consCompanyMandante, setConsCompanyMandante] = useState('all');
  const [consCompany, setConsCompany] = useState('');
  const [dayCompanies, setDayCompanies] = useState([]);
  const [dayCompaniesLoading, setDayCompaniesLoading] = useState(false);
  const [consDayDate, setConsDayDate] = useState(null);
  const [consDayMandante, setConsDayMandante] = useState('all');
  const [consBusy, setConsBusy] = useState(false);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    return () => { if (previewUrl) window.URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  // Cargar empresas que tuvieron capacitación (con resultado) ese día
  useEffect(() => {
    setConsCompany('');
    setDayCompanies([]);
    if (!consCompanyDate) return;
    let cancelled = false;
    (async () => {
      setDayCompaniesLoading(true);
      try {
        const params = { date: format(consCompanyDate, 'yyyy-MM-dd') };
        if (consCompanyMandante !== 'all') params.mandante_id = consCompanyMandante;
        const res = await certificatesApi.consolidatedCompaniesForDay(params);
        if (!cancelled) setDayCompanies(res.data || []);
      } catch {
        if (!cancelled) setDayCompanies([]);
      } finally {
        if (!cancelled) setDayCompaniesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [consCompanyDate, consCompanyMandante]);

  const handlePreview = async (cert) => {
    setPreviewCert(cert);
    setPreviewTitle(`Certificado ${cert.code}${cert.worker_name ? ' — ' + cert.worker_name : ''}`);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl('');
    try {
      const response = await certificatesApi.preview(cert.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch {
      toast.error('Error al cargar la vista previa del certificado');
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [certsRes, compsRes] = await Promise.all([
        certificatesApi.getAll(),
        companiesApi.getAll({ page_size: 999 })
      ]);
      setCertificates(certsRes.data);
      const allComps = compsRes.data.items || compsRes.data;
      setCompanies(allComps.filter(c => !c.is_client));
      setMandantes(allComps.filter(c => c.is_client));
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const handleDownload = async (cert) => {
    try {
      const response = await certificatesApi.download(cert.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificado_${cert.code}.pdf`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificado descargado');
    } catch { toast.error('Error al descargar certificado'); }
  };

  // ---- Consolidated handlers ----
  const buildConsParams = (type) => {
    const params = {};
    if (type === 'company') {
      params.date = format(consCompanyDate, 'yyyy-MM-dd');
      params.company_id = consCompany;
      if (consCompanyMandante !== 'all') params.mandante_id = consCompanyMandante;
    } else {
      params.date = format(consDayDate, 'yyyy-MM-dd');
      if (consDayMandante !== 'all') params.mandante_id = consDayMandante;
    }
    return params;
  };

  const consValid = (type) => {
    if (type === 'company') return !!consCompanyDate && !!consCompany;
    return !!consDayDate;
  };

  const handleConsPreview = async (type) => {
    if (!consValid(type)) { toast.error('Selecciona los datos requeridos'); return; }
    setConsBusy(true);
    setPreviewUrl('');
    setPreviewCert(null);
    setPreviewTitle(type === 'company' ? 'Certificado consolidado por empresa' : 'Certificado consolidado por día');
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const api = type === 'company' ? certificatesApi.consolidatedByCompany : certificatesApi.consolidatedByDay;
      const res = await api({ ...buildConsParams(type), download: false });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (err) {
      const msg = err?.response?.status === 404 ? 'No hay asistentes con resultado para esa selección' : 'Error al generar el certificado';
      toast.error(msg);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
      setConsBusy(false);
    }
  };

  const handleConsDownload = async (type) => {
    if (!consValid(type)) { toast.error('Selecciona los datos requeridos'); return; }
    setConsBusy(true);
    try {
      const api = type === 'company' ? certificatesApi.consolidatedByCompany : certificatesApi.consolidatedByDay;
      const res = await api({ ...buildConsParams(type), download: true });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const d = type === 'company' ? consCompanyDate : consDayDate;
      link.setAttribute('download', `consolidado_${type}_${format(d, 'yyyyMMdd')}.pdf`);
      document.body.appendChild(link);
      link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Certificado descargado');
    } catch (err) {
      const msg = err?.response?.status === 404 ? 'No hay asistentes con resultado para esa selección' : 'Error al descargar el certificado';
      toast.error(msg);
    } finally {
      setConsBusy(false);
    }
  };

  const filteredCertificates = certificates.filter(c => {
    const matchesSearch = c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.worker_name && c.worker_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.training_name && c.training_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (c.worker_rut && c.worker_rut.includes(searchTerm));
    const matchesCompany = companyFilter === 'all' || c.company_name === companyFilter;
    const matchesDate = !dateFilter || (c.training_date && isSameDay(new Date(c.training_date), dateFilter));
    return matchesSearch && matchesCompany && matchesDate;
  });

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" /></div>);
  }

  const DatePickerField = ({ value, onChange, testid }) => (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`w-full justify-start text-left font-normal bg-[#f8fafc] border-[#2B4B8C]/10 ${value ? 'text-[#2B4B8C]' : 'text-[#2B4B8C]/50'}`} data-testid={testid}>
          <CalendarIcon size={16} className="mr-2 shrink-0" />
          {value ? format(value, "dd 'de' MMMM yyyy", { locale: es }) : 'Selecciona la fecha'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-[#2B4B8C]/10" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} locale={es} className="rounded-md" />
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="certificates-page">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Certificados</h1>
        <p className="text-[#2B4B8C]/60">Emisión de certificados individuales y consolidados</p>
      </div>

      <Tabs defaultValue="individuales" className="w-full">
        <TabsList className="bg-[#f1f5f9] border border-[#2B4B8C]/10">
          <TabsTrigger value="individuales" data-testid="tab-individuales">Individuales</TabsTrigger>
          <TabsTrigger value="por-empresa" data-testid="tab-por-empresa">Por empresa por día</TabsTrigger>
          <TabsTrigger value="por-dia" data-testid="tab-por-dia">Por día</TabsTrigger>
        </TabsList>

        {/* ---------- INDIVIDUALES ---------- */}
        <TabsContent value="individuales" className="space-y-6 mt-4">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2B4B8C]/60" size={20} />
                  <Input placeholder="Buscar por código, trabajador, RUT o capacitación..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" />
                </div>
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="w-full sm:w-52 bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="company-filter">
                    <SelectValue placeholder="Todas las empresas" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#2B4B8C]/10">
                    <SelectItem value="all">Todas las empresas</SelectItem>
                    {companies.map(c => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={`w-full sm:w-56 justify-start text-left font-normal bg-[#f8fafc] border-[#2B4B8C]/10 ${dateFilter ? 'text-[#2B4B8C]' : 'text-[#2B4B8C]/50'}`} data-testid="date-filter-btn">
                      <CalendarIcon size={16} className="mr-2 shrink-0" />
                      {dateFilter ? format(dateFilter, "dd 'de' MMMM yyyy", { locale: es }) : 'Fecha capacitación'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#2B4B8C]/10" align="end">
                    <Calendar mode="single" selected={dateFilter} onSelect={setDateFilter} locale={es} className="rounded-md" />
                  </PopoverContent>
                </Popover>
                {dateFilter && (
                  <Button variant="ghost" size="sm" onClick={() => setDateFilter(null)} className="text-red-400 hover:text-red-500 shrink-0" data-testid="clear-date-filter">
                    <XCircle size={18} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#2B4B8C]/10 hover:bg-transparent">
                    <TableHead className="text-[#2B4B8C]/60">Código</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Trabajador</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">RUT</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Empresa</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Capacitación</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Nº Vez</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Fecha Cap.</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Vigencia Hasta</TableHead>
                    <TableHead className="text-[#2B4B8C]/60">Estado</TableHead>
                    <TableHead className="text-[#2B4B8C]/60 text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCertificates.map((cert) => (
                    <TableRow key={cert.id} className="border-[#2B4B8C]/5 hover:bg-[#f8fafc]">
                      <TableCell>
                        <button type="button" onClick={() => handlePreview(cert)} className="flex items-center gap-2 group" data-testid={`view-code-${cert.id}`}>
                          <Certificate size={16} className="text-[#2BBAA4]" />
                          <span className="text-[#2B4B8C] font-mono text-xs group-hover:text-[#2BBAA4] group-hover:underline">{cert.code}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-[#2B4B8C] font-medium">{cert.worker_name}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{cert.worker_rut || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60">{cert.company_name || '-'}</TableCell>
                      <TableCell className="text-[#2B4B8C]/60 max-w-xs truncate">{cert.training_name}</TableCell>
                      <TableCell><CourseCountBadge count={cert.course_count} /></TableCell>
                      <TableCell className="text-[#2B4B8C]/60">
                        {cert.training_date ? format(new Date(cert.training_date), "dd MMM yyyy", { locale: es }) : '-'}
                      </TableCell>
                      <TableCell className="text-[#2B4B8C]/60">
                        {cert.expiry_date ? format(new Date(cert.expiry_date), "dd MMM yyyy", { locale: es }) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={cert.is_valid ? 'bg-green-500/10 text-green-400 border-green-500/20 border' : 'bg-red-500/10 text-red-400 border-red-500/20 border'}>
                          {cert.is_valid ? 'Válido' : 'Inválido'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(cert)} className="text-[#2B4B8C]/60 hover:text-[#2BBAA4] hover:bg-[#2BBAA4]/10" data-testid={`view-cert-${cert.id}`} title="Ver certificado">
                            <Eye size={18} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(cert)} className="text-[#2B4B8C]/60 hover:text-[#2BBAA4] hover:bg-[#2BBAA4]/10" data-testid={`download-cert-${cert.id}`} title="Descargar certificado">
                            <Download size={18} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {filteredCertificates.length === 0 && (
                <div className="text-center py-12 text-[#2B4B8C]/50">No se encontraron certificados</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- POR EMPRESA POR DÍA ---------- */}
        <TabsContent value="por-empresa" className="mt-4">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6 space-y-5 max-w-2xl">
              <div className="flex items-center gap-2 text-[#2B4B8C]">
                <Buildings size={20} className="text-[#2BBAA4]" />
                <h2 className="text-lg font-semibold">Certificado consolidado por empresa (de un día)</h2>
              </div>
              <p className="text-sm text-[#2B4B8C]/60">Genera un único certificado (vertical) con todos los asistentes con resultado de una empresa en la fecha elegida.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#2B4B8C]/80">Fecha de capacitación *</Label>
                  <DatePickerField value={consCompanyDate} onChange={setConsCompanyDate} testid="cons-company-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#2B4B8C]/80">Mandante (opcional)</Label>
                  <Select value={consCompanyMandante} onValueChange={setConsCompanyMandante}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cons-company-mandante"><SelectValue placeholder="Todos los mandantes" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      <SelectItem value="all">Todos los mandantes</SelectItem>
                      {mandantes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-[#2B4B8C]/80">Empresa contratista *</Label>
                  <Select value={consCompany} onValueChange={setConsCompany} disabled={!consCompanyDate || dayCompaniesLoading}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cons-company-select">
                      <SelectValue placeholder={!consCompanyDate ? 'Primero elige una fecha' : (dayCompaniesLoading ? 'Cargando empresas...' : (dayCompanies.length ? 'Selecciona la empresa' : 'Sin empresas con resultado ese día'))} />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10 max-h-72">
                      {dayCompanies.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                  {consCompanyDate && !dayCompaniesLoading && (
                    <p className="text-xs text-[#2B4B8C]/40">{dayCompanies.length} empresa(s) con asistentes con resultado ese día</p>
                  )}
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button variant="outline" disabled={consBusy} onClick={() => handleConsPreview('company')} className="border-[#2B4B8C]/20 text-[#2B4B8C]" data-testid="cons-company-preview">
                  <Eye size={16} className="mr-1.5" /> Vista previa
                </Button>
                <Button disabled={consBusy} onClick={() => handleConsDownload('company')} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white" data-testid="cons-company-download">
                  <Download size={16} className="mr-1.5" /> Descargar PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- POR DÍA ---------- */}
        <TabsContent value="por-dia" className="mt-4">
          <Card className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6 space-y-5 max-w-2xl">
              <div className="flex items-center gap-2 text-[#2B4B8C]">
                <UsersThree size={20} className="text-[#2BBAA4]" />
                <h2 className="text-lg font-semibold">Certificado consolidado por día (todas las empresas)</h2>
              </div>
              <p className="text-sm text-[#2B4B8C]/60">Genera un único certificado (vertical) con todos los asistentes con resultado del día, incluyendo la columna Empresa.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-[#2B4B8C]/80">Fecha de capacitación *</Label>
                  <DatePickerField value={consDayDate} onChange={setConsDayDate} testid="cons-day-date" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[#2B4B8C]/80">Mandante (opcional)</Label>
                  <Select value={consDayMandante} onValueChange={setConsDayMandante}>
                    <SelectTrigger className="bg-[#f8fafc] border-[#2B4B8C]/10 text-[#2B4B8C]" data-testid="cons-day-mandante"><SelectValue placeholder="Todos los mandantes" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#2B4B8C]/10">
                      <SelectItem value="all">Todos los mandantes</SelectItem>
                      {mandantes.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button variant="outline" disabled={consBusy} onClick={() => handleConsPreview('day')} className="border-[#2B4B8C]/20 text-[#2B4B8C]" data-testid="cons-day-preview">
                  <Eye size={16} className="mr-1.5" /> Vista previa
                </Button>
                <Button disabled={consBusy} onClick={() => handleConsDownload('day')} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white" data-testid="cons-day-download">
                  <Download size={16} className="mr-1.5" /> Descargar PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Modal (individual + consolidado) */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl w-[95vw] p-0 bg-white overflow-hidden" data-testid="certificate-preview-modal">
          <DialogHeader className="px-5 pt-4 pb-3 border-b border-[#2B4B8C]/10">
            <DialogTitle className="text-[#2B4B8C] flex items-center gap-2">
              <Certificate size={20} className="text-[#2BBAA4]" />
              {previewTitle || 'Certificado'}
            </DialogTitle>
          </DialogHeader>
          <div className="bg-[#525659] h-[75vh] flex items-center justify-center">
            {previewLoading ? (
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2BBAA4]" />
            ) : previewUrl ? (
              <iframe src={previewUrl} title="Vista previa del certificado" className="w-full h-full border-0" data-testid="certificate-preview-iframe" />
            ) : (
              <p className="text-white/70 text-sm">No se pudo cargar el certificado</p>
            )}
          </div>
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#2B4B8C]/10">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="border-[#2B4B8C]/20 text-[#2B4B8C]">Cerrar</Button>
            {previewCert && (
              <Button onClick={() => handleDownload(previewCert)} className="bg-[#2BBAA4] hover:bg-[#249b89] text-white" data-testid="modal-download-btn">
                <Download size={16} className="mr-1.5" /> Descargar
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
